import { Button } from '../../components/v3';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import { SuccessIcon } from '../../assets';
import {
  CommonDetails
} from '../../constants';

import dayjs from 'dayjs';
import { useTealium } from '../../tealium/useTealium';
import styles from './OtpEntryModal.module.css';

const OTP_LENGTH = 6;
const RE_SINGLE_NUMERIC = new RegExp(/^[0-9]{1}$/);
const RE_SIX_NUMERIC = /^[0-9]{6}$/;
const OTPTIMEEXPIRYINMINS = 5;

const rightButtonStyle = {
  borderRadius: '50px',
  backgroundColor: '#007C92'
};
const disabledBtnStyle = {
  backgroundColor: '#C9CCD4',
  borderRadius: '50px'
};

const calculateTimeLeft = (data) => {

  if (!data || isNaN(data)) {
    return null;
  }
  const now = dayjs();
  const blockedUnixTime = dayjs.unix(data);
  const difference = blockedUnixTime?.diff?.(now);

  if (difference <= 0 || !difference || isNaN(difference)) {
    return 0;
  }

  const minutes = Math.floor(difference / 1000 / 60);
  const seconds = Math.floor((difference / 1000) % 60);

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const addOTPTimer = () => {
  return dayjs().add(OTPTIMEEXPIRYINMINS, 'minute').unix();
};

export const OtpEntryModal = ({
  maskedContact,
  verifyOtpResponse,
  otpResponse,
  resendOtp,
  submitOtp,
  resetAllResponse,
  onView,
  closeOtpModal
}) => {

  const [otp, setOtp] = useState(' '.repeat(OTP_LENGTH));
  const [nextFocusIndex, setNextFocusIndex] = useState(null);
  const [_resendState, setResendState] = useState(false);
  const [modalToShow, setModalToShow] = useState('OTPSCREEN');
  const smallWindowComponents = ['OTPEXPIRED', 'LASTATTEMPT', 'LIMITREACHED'];
  const [otpExpirationTime, setOtpExpirationTime] = useState(otpResponse?.OTPExpiresIn || addOTPTimer());
  const [isOtpExpired, setIsOtpExpired] = useState(false);

  const otpAttemptsExceeded = useMemo(() => !!(verifyOtpResponse?.errorData?.otpAttemptsRemaining === 0 ||
    verifyOtpResponse?.message === 'OTPAttemptsBlocked' || otpResponse?.message === 'OTPAttemptsBlocked'), [otpResponse, verifyOtpResponse]);

  const resendAttemptsExceeded = useMemo(() => !!(otpResponse?.otpResendRemaining === 0 ||
    otpResponse?.message === 'OTPResendBlocked'), [otpResponse]);

  const resetOtp = () => setOtp(' '.repeat(OTP_LENGTH));
  const { track, events } = useTealium();

  const isOtpNumeric = useMemo(() => RE_SIX_NUMERIC.test(otp), [otp]);

  useEffect(() => {
    if (verifyOtpResponse?.errorData?.code || verifyOtpResponse?.message === 'OTPExpired') {
      resetOtp();
      setModalToShow('OTPEXPIRED');
    }
    if (verifyOtpResponse?.otpResendRemaining === 1 || verifyOtpResponse?.otpAttemptsRemaining === 1) {
      setModalToShow('LASTATTEMPT');
    }
    if (verifyOtpResponse?.message === 'CodeMismatch' && (verifyOtpResponse?.errorData?.otpAttemptsRemaining === 1 || verifyOtpResponse?.otpResendRemaining === 1)) {
      setModalToShow('LASTATTEMPT');
    }

    if (verifyOtpResponse?.message === 'CodeMismatch' && (verifyOtpResponse?.errorData?.otpAttemptsRemaining > 1 || verifyOtpResponse?.otpResendRemaining > 1)) {
      setModalToShow('OTPRETRY');
    }

  }, [verifyOtpResponse]);

  useEffect(() => {
    onView();
  }, []);

  const requestResendOtp = () => {
    setResendState(true);
    track(events.otp.resend());
    resendOtp();
    setModalToShow('OTPRESEND');
    setOtpExpirationTime(addOTPTimer());
    setIsOtpExpired(false);
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  const isAttemptBlocked = otpAttemptsExceeded || resendAttemptsExceeded;

  /* eslint-disable consistent-return */
  useEffect(() => {
    const verifyRespTime = calculateTimeLeft(verifyOtpResponse?.errorData?.minutesBlocked);
    const otpRespTime = calculateTimeLeft(otpResponse?.errorData?.minutesBlocked);
    const otpInitialRespTime = calculateTimeLeft(otpResponse?.minsToBlock);
    const verifyInitialRespTime = calculateTimeLeft(verifyOtpResponse?.errorData?.minsToBlock);

    let timeToBlock;

    if (verifyRespTime !== null) {
      timeToBlock = verifyOtpResponse?.errorData?.minutesBlocked;
    } else if (otpRespTime !== null) {
      timeToBlock = otpResponse?.errorData?.minutesBlocked;
    } else if (otpInitialRespTime !== null) {
      timeToBlock = otpResponse?.minsToBlock;
    } else if (verifyInitialRespTime !== null) {
      timeToBlock = verifyOtpResponse?.errorData?.minsToBlock;
    }

    if (isAttemptBlocked) {

      setModalToShow('LIMITREACHED');
      track(events.error('otp', 'otp_exceeded', 'OTP attempts exceeded'));

      const timer = setInterval(() => {

        setTimeLeft(calculateTimeLeft(timeToBlock));
      }, 1000);

      return () => clearInterval(timer);
    }

  }, [verifyOtpResponse, otpResponse, isAttemptBlocked]);

  useEffect(() => {
    if (otpResponse) {
      resetOtp();
    }

    if (otpResponse?.otpResendRemaining === 1 || otpResponse?.otpAttemptsRemaining === 1) {
      setModalToShow('LASTATTEMPT');
      track(events.otp.lastAttempt());
    }

    if (otpResponse?.isResend && (otpResponse?.otpResendRemaining > 1 || otpResponse?.otpAttemptsRemaining > 1)) {
      setModalToShow('OTPRESEND');
      setOtpExpirationTime(addOTPTimer());
      setIsOtpExpired(false);
    }

  }, [otpResponse]);

  useEffect(() => {

    if (timeLeft === 0) {
      setTimeLeft(null);
      track(events.otp.expired());
      closeOtpModal();
    }
  }, [timeLeft]);

  useEffect(() => {
    if (otpAttemptsExceeded || resendAttemptsExceeded) {
      setModalToShow('LIMITREACHED');
    }
  }, [otpAttemptsExceeded, resendAttemptsExceeded]);

  const continueClick = () => {
    track(events.otp.continue());
    submitOtp(otp);
  };

  const renderPopupContent = () => {
    switch (modalToShow) {
      case 'OTPSCREEN':
        return <OTPScreen />;
      case 'OTPRETRY':
        return <OTPRetry />;
      case 'OTPRESEND':
        return <OTPResend />;
      case 'LIMITREACHED':
        return <LimitReached />;
      case 'OTPEXPIRED':
        return <OTPExpired />;
      case 'LASTATTEMPT':
        return <LastAttempt />;
      default:
        return <OTPScreen />;
    }
  };

  const LimitReached = () => {
    return (
      <>
        <div className={styles.mainContainerOTPExpired}>
          <OrangeInfoIcon />
          <div className={styles.Negativeheading}>
            OTP limit reached. Please wait.
          </div>
          <div className={styles.incorrectOtpMsg}>
            You&apos;ve entered the incorrect OTP too many times. For security reasons, your access is temporarily locked.
          </div>
          <div className={styles.incorrectOtpBlockedMsg}>
            Please try again in:
          </div>
          <div className={styles.incorrectOtpTimeout}>
            {timeLeft}
          </div>
          <SupportArea />
        </div>
      </>
    );
  };

  const OTPExpired = () => {
    return (
      <>
        <div className={styles.mainContainerOTPExpired}>
          <OrangeInfoIcon />
          <div className={styles.Negativeheading}>
            OTP expired
          </div>
          <div className={styles.incorrectOtpMsg}>
            The OTP has expired. Please request a new one to continue.
          </div>
          <div className={styles.lastAttemptButton}>
            <Button
              style={rightButtonStyle}
              className={styles.continueBtn}
              as={'button'}
              onClick={requestResendOtp}
            >
              Request a new OTP
            </Button>
          </div>
          <SupportArea />
        </div>
      </>
    );
  };

  const LastAttempt = () => {
    return (
      <>
        <div className={styles.mainContainerOTPExpired}>
          <OrangeInfoIcon />
          <div className={styles.Negativeheading}>
            Almost out of tries
          </div>
          <div className={styles.incorrectOtpMsg}>
            You have 1 attempt left to enter the correct OTP. After this, you&apos;ll be locked out for security reasons.
          </div>
          <div className={styles.lastAttemptButton}>
            <Button
              style={rightButtonStyle}
              className={styles.continueBtn}
              as={'button'}
              onClick={nextClick}
            >
              Retry
            </Button>
          </div>
          <SupportArea />
        </div>
      </>
    );
  };
  const nextClick = () => {
    if (verifyOtpResponse?.message === 'CodeMismatch') {
      setModalToShow('OTPRETRY');
    } else if (otpResponse?.isResend) {
      setModalToShow('OTPRESEND');
      setOtpExpirationTime(addOTPTimer());
      setIsOtpExpired(false);
    } else {
      setModalToShow('OTPSCREEN');
      setOtpExpirationTime(addOTPTimer());
      setIsOtpExpired(false);
    }
  };

  const ContinueButton = () => {
    return (
      <>
        <Button
          style={isOtpNumeric ? rightButtonStyle : disabledBtnStyle}
          className={isOtpNumeric ? styles.continueBtn : styles.continueBtnDisabled}
          disabled={!isOtpNumeric}

          as={'button'}
          onClick={continueClick}
        >
          Continue
        </Button>
      </>
    );
  };

  const OTPRetry = () => {

    return (
      <div className={styles.mainContainer}>
        <OrangeInfoIcon />
        <div className={styles.Negativeheading}>
          Incorrect OTP entered - try again
        </div>
        <MaskedContact />
        <OTPInput otp={otp} setOtp={setOtp} submitOtp={submitOtp} nextFocusIndex={nextFocusIndex} setNextFocusIndex={setNextFocusIndex} />

        {!isOtpExpired && otpExpirationTime && (
          <CountdownTimer
            expiryUnix={otpExpirationTime}
            onExpire={() => {
              setIsOtpExpired(true);
              resetAllResponse();
              setModalToShow('OTPEXPIRED');
            }}
          />
        )}
        <ContinueButton />
        <ResendLink />
        <SupportArea />
      </div>
    );
  };

  const OTPResend = () => {
    return (
      <div className={styles.mainContainer}>
        < OTPResentIcon />
        <div className={styles.heading}>
          OTP resent
        </div>
        <MaskedContact />
        <OTPInput otp={otp} setOtp={setOtp} submitOtp={submitOtp} nextFocusIndex={nextFocusIndex} setNextFocusIndex={setNextFocusIndex} />

        {!isOtpExpired && otpExpirationTime && (
          <CountdownTimer
            expiryUnix={otpExpirationTime}
            onExpire={() => {
              setIsOtpExpired(true);
              resetAllResponse();
              setModalToShow('OTPEXPIRED');
            }}
          />
        )}
        <ContinueButton />
        <ResendLink />
        <SupportArea />
      </div>
    );
  };

  const OrangeInfoIcon = () => {
    return (
      <div className={styles.warningImg}>
        <div className={styles.exclamationLine} />
        <div className={styles.exclamationDot} />
      </div>
    );
  };

  const ResendLink = () => {
    return (
      <div className={styles.resendOtpText}>
        Didn’t receive an OTP?
        <span
          className={styles.resendLink}
          onClick={requestResendOtp}
          onKeyDown={requestResendOtp}
          role='presentation'
        >
          Resend
        </span>
      </div>
    );
  };

  const MaskedContact = () => {
    return (
      <div className={styles.subHeading}>
        <div>
          Please enter {OTP_LENGTH} digit verification code sent to
        </div>
        <div>
          <div className={styles.subHeadingHiddenMsisdn}>
            {maskedContact || otpResponse?.contact}
          </div>
        </div>
      </div>
    );
  };

  const OTPScreen = () => {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.heading}>
          We sent you an SMS
        </div>
        <MaskedContact />
        <OTPInput otp={otp} setOtp={setOtp} submitOtp={submitOtp} nextFocusIndex={nextFocusIndex} setNextFocusIndex={setNextFocusIndex} />
        {!isOtpExpired && otpExpirationTime && (
          <CountdownTimer
            expiryUnix={otpExpirationTime}
            onExpire={() => {
              setIsOtpExpired(true);
              resetAllResponse();
              setModalToShow('OTPEXPIRED');
            }}
          />
        )}
        <ContinueButton />
        <ResendLink />
        <SupportArea />
      </div>
    );
  };

  const SupportArea = () => {

    const triggerEmailClient = () => {
      track(events.otp.emailHelp());
      window.location.href = `mailto:${CommonDetails.contactUsEmail}`;
    };

    return (
      <div className={styles.needAssistanceBlock}>
        <div className={styles.needAssistanceTxt}>
          Need help?
        </div>
        <div className={styles.contactUsLink}>
          Email <span className={styles.contactUsEmail}
            onClick={triggerEmailClient}
            onKeyDown={triggerEmailClient}
            role='presentation'
          > <u> {CommonDetails.contactUsEmail} </u>
          </span>
        </div>
      </div>
    );
  };

  const OTPResentIcon = () => (
    <div>
      <img src={SuccessIcon} alt="success icon" className={styles.titleIcons} />
    </div>
  );

  return (
    <div className={smallWindowComponents.includes(modalToShow) ? styles.mainContainerOTPExpired : styles.mainContainer}>

      {renderPopupContent()}
    </div>
  );
};

OtpEntryModal.propTypes = {
  submitOtp: PropTypes.func,
  resendOtp: PropTypes.func,
  maskedContact: PropTypes.string,
  verifyOtpResponse: PropTypes.object,
  otpResponse: PropTypes.object,
  resetAllResponse: PropTypes.func,
  onView: PropTypes.func,
  closeOtpModal: PropTypes.func
};

const CountdownTimer = ({ expiryUnix, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(expiryUnix));

  useEffect(() => {
    if (!expiryUnix) {return;}

    const interval = setInterval(() => {
      const updated = calculateTimeLeft(expiryUnix);
      setTimeLeft(updated);
      if (updated === '00:00' || updated === 0) {
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryUnix, onExpire]);

  if (!timeLeft) {return null;}

  return <div className={styles.otpTimerText}>Your code will expire in {timeLeft}</div>;
};

CountdownTimer.propTypes = {
  expiryUnix: PropTypes.number,
  onExpire: PropTypes.func
};

const OTPInput = ({ otp, setOtp, submitOtp, nextFocusIndex, setNextFocusIndex }) => {
  const valueItems = useMemo(() => otp && otp.split(''), [otp]);

  useEffect(() => {
    if (nextFocusIndex !== null && inputRefs.current[nextFocusIndex]) {
      inputRefs.current[nextFocusIndex].focus();
    }
  }, [nextFocusIndex]);

  const inputRefs = useRef([]);

  const inputOnChange = (e, idx) => {

    const target = e.target;
    let targetValue = target.value.trim();
    const isTargetValue = RE_SINGLE_NUMERIC.test(targetValue);

    targetValue = isTargetValue ? targetValue : ' ';

    const targetValueLength = targetValue.length;

    if (targetValueLength === 1) {
      const newValue = otp.substring(0, idx) + targetValue + otp.substring(idx + 1);
      setOtp(newValue);

      if (isTargetValue) {
        setNextFocusIndex(idx + 1);
      }
    } else if (targetValueLength === otp.length) {
      setOtp(targetValue);
      target.blur();
    }

    return '';
  };

  const inputOnKeyDown = (e, idx) => {

    const { key, target } = e;

    const targetValue = target.value;

    if (key === 'ArrowRight' || key === 'ArrowDown') {

      e.preventDefault();

      setNextFocusIndex(idx + 1);

    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {

      e.preventDefault();
      setNextFocusIndex(idx - 1);

    } else if (targetValue === ' ' && key === 'Backspace') {

      setNextFocusIndex(idx - 1);
    } else if (key === 'Backspace') {
      setNextFocusIndex(idx);
    } else if (key === 'Enter' && RE_SIX_NUMERIC.test(otp)) {

      e.preventDefault();
      if (typeof submitOtp === 'function') {
        submitOtp(otp);
      }
    }

    target.setSelectionRange(0, targetValue.length);

    if (key !== 'Backspace' || targetValue !== '') {

      return '';
    }

    setNextFocusIndex(idx - 1);
  };

  const inputOnFocus = (e) => {
    const { target } = e;

    const prevInputEl =
      target.previousElementSibling;

    if (prevInputEl && prevInputEl.value === '') {
      return prevInputEl.focus();
    }

    return target.setSelectionRange(0, target.value.length);
  };

  const handlePaste = (e) => {

    e.preventDefault();

    const pasteData = e.clipboardData.getData('text/plain').slice(0, OTP_LENGTH).trim();

    const pasteDataLength = pasteData.length;

    if (pasteDataLength !== OTP_LENGTH || !RE_SIX_NUMERIC.test(pasteData)) {
      return;
    }

    const lastElement = document.getElementById(`otpInput-${OTP_LENGTH}`);

    if (lastElement) {
      lastElement?.focus?.();
    }

    setOtp(pasteData);
  };

  return (
    <div className={styles.otpEntryContainer}>
      {valueItems.map((digit, idx) => (
        <input
          type='text'
          key={idx}
          maxLength={1}
          className={`${styles.otpEntryBox} ${RE_SINGLE_NUMERIC.test(digit) ? styles.otpEntryBoxValue : ''}`}
          onPaste={handlePaste}
          ref={(el) => (inputRefs.current[idx] = el)}
          onChange={(e) => inputOnChange(e, idx)}
          onKeyDown={(e) => inputOnKeyDown(e, idx)}
          onFocus={inputOnFocus}
          value={digit}
          id={`otpInput-${idx + 1}`}
        />
      ))}
    </div>
  );
};

OTPInput.propTypes = {
  otp: PropTypes.number,
  setOtp: PropTypes.func,
  submitOtp: PropTypes.func,
  nextFocusIndex: PropTypes.number,
  setNextFocusIndex: PropTypes.func
};